import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  getDoc,
  updateDoc
} from "firebase/firestore";
import { db, auth } from "../firebase";

const ProjectSelection = () => {
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newProjectName, setNewProjectName] = useState("");
  const [newProjectDriveLink, setNewProjectDriveLink] = useState("");
  const [addingNew, setAddingNew] = useState(false);
  const [userData, setUserData] = useState(null);
  const [projectProgress, setProjectProgress] = useState({});

  const [statusFilter, setStatusFilter] = useState("todos");
  const [searchQuery, setSearchQuery] = useState("");

  const [editingProjectId, setEditingProjectId] = useState(null);
  const [editProjectData, setEditProjectData] = useState({
    name: "",
    client: "",
    driveLink: ""
  });

  useEffect(() => {
    const fetchUserAndProjects = async () => {
      try {
        const userRef = doc(db, "users", auth.currentUser.uid);
        const userSnap = await getDoc(userRef);

        if (!userSnap.exists()) {
          throw new Error("Usuário não encontrado no banco de dados.");
        }

        const user = userSnap.data();
        setUserData(user);

        let allowedProjectIds = [];

        if (user.role === "admin") {
          const projectsSnap = await getDocs(collection(db, "projects"));
          allowedProjectIds = projectsSnap.docs.map((doc) => doc.id);
        } else {
          allowedProjectIds = user.allowedProjects || [];
        }

        const projectsList = [];
        const progressMap = {};

        for (const projectId of allowedProjectIds) {
          try {
            const projDoc = await getDoc(doc(db, "projects", projectId));
            if (projDoc.exists()) {
              const projData = projDoc.data();
              const fullProject = { id: projDoc.id, ...projData };
              projectsList.push(fullProject);

              const stagesCol = collection(db, "projects", projectId, "stages");
              const stagesSnap = await getDocs(stagesCol);
              let totalActivities = 0;
              let completedActivities = 0;

              for (const stageDoc of stagesSnap.docs) {
                const stageData = stageDoc.data();
                const activities = stageData.activities || [];
                activities.forEach((act) => {
                  totalActivities += 1;
                  if (act.status === "Concluído") {
                    completedActivities += 1;
                  }
                });
              }

              const percent =
                totalActivities === 0
                  ? 0
                  : Math.round((completedActivities / totalActivities) * 100);
              progressMap[projectId] = percent;
            }
          } catch (e) {
            console.warn(`Erro ao carregar projeto ${projectId}:`, e);
          }
        }

        setProjects(projectsList);
        setProjectProgress(progressMap);
        setLoading(false);
      } catch (err) {
        console.error("Erro ao carregar projetos:", err);
        setError("Erro ao carregar projetos.");
        setLoading(false);
      }
    };

    fetchUserAndProjects();
  }, []);

  const addProject = async () => {
    if (!newProjectName.trim()) return;

    try {
      const newProject = {
        name: newProjectName.trim(),
        client: "",
        driveLink: newProjectDriveLink.trim(),
        createdAt: new Date()
      };

      const docRef = await addDoc(collection(db, "projects"), newProject);
      setProjects((prev) => [...prev, { id: docRef.id, ...newProject }]);
      setProjectProgress((prev) => ({ ...prev, [docRef.id]: 0 }));

      setNewProjectName("");
      setNewProjectDriveLink("");
      setAddingNew(false);
    } catch (err) {
      alert("Erro ao adicionar projeto");
    }
  };

  const deleteProject = async (projectId) => {
    if (!window.confirm("Deseja realmente deletar este projeto?")) return;

    try {
      await deleteDoc(doc(db, "projects", projectId));
      setProjects((prev) => prev.filter((p) => p.id !== projectId));
      setProjectProgress((prev) => {
        const copy = { ...prev };
        delete copy[projectId];
        return copy;
      });
    } catch (err) {
      alert("Erro ao deletar projeto");
    }
  };

  const cloneProject = async (project) => {
    if (!window.confirm(`Deseja clonar o projeto "${project.name}"?`)) return;

    try {
      const newProject = {
        name: `Clone de: ${project.name}`,
        client: project.client,
        driveLink: project.driveLink || "",
        createdAt: new Date()
      };

      const newProjRef = await addDoc(collection(db, "projects"), newProject);

      const stagesCol = collection(db, "projects", project.id, "stages");
      const stagesSnap = await getDocs(stagesCol);

      for (const stageDoc of stagesSnap.docs) {
        const stageData = stageDoc.data();
        await addDoc(collection(db, "projects", newProjRef.id, "stages"), {
          name: stageData.name,
          activities: stageData.activities || []
        });
      }

      setProjects((prev) => [...prev, { id: newProjRef.id, ...newProject }]);
      setProjectProgress((prev) => ({ ...prev, [newProjRef.id]: 0 }));

      alert(`Projeto "${project.name}" clonado com sucesso!`);
    } catch (err) {
      console.error(err);
      alert("Erro ao clonar projeto");
    }
  };

  const startEditing = (project) => {
    setEditingProjectId(project.id);
    setEditProjectData({
      name: project.name,
      client: project.client || "",
      driveLink: project.driveLink || ""
    });
  };

  const updateProject = async (projectId) => {
    try {
      const projectRef = doc(db, "projects", projectId);
      await updateDoc(projectRef, {
        name: editProjectData.name,
        client: editProjectData.client,
        driveLink: editProjectData.driveLink
      });

      setProjects((prev) =>
        prev.map((p) => (p.id === projectId ? { ...p, ...editProjectData } : p))
      );

      setEditingProjectId(null);
      alert("Projeto atualizado com sucesso!");
    } catch (err) {
      console.error(err);
      alert("Erro ao atualizar projeto.");
    }
  };

  if (loading)
    return (
      <p className="text-gray-600 text-center mt-20">
        Carregando projetos...
      </p>
    );
  if (error)
    return <p className="text-red-500 text-center mt-20">{error}</p>;

  const filteredProjects = projects.filter((project) => {
    const progress = projectProgress[project.id] ?? 0;
    const status = progress === 100 ? "concluido" : "andamento";

    const matchesStatus = statusFilter === "todos" || status === statusFilter;

    const searchLower = searchQuery.toLowerCase();
    const matchesSearch =
      project.name.toLowerCase().includes(searchLower) ||
      (project.client || "").toLowerCase().includes(searchLower);

    return matchesStatus && matchesSearch;
  });

  return (
    <div className="bg-gray-50 p-4">
      <div className="max-w-5xl mx-auto bg-white p-8 rounded-2xl shadow-lg space-y-6">
        <h2 className="text-2xl font-bold text-gray-800">Seus Projetos</h2>

        <div className="flex flex-col sm:flex-row gap-4">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border px-3 py-2 rounded"
          >
            <option value="todos">Todos</option>
            <option value="andamento">Em andamento</option>
            <option value="concluido">Concluído</option>
          </select>

          <input
            type="text"
            placeholder="Buscar por projeto ou cliente"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="border px-3 py-2 rounded flex-1"
          />
        </div>

        <div className="space-y-4">
          {filteredProjects.length === 0 && (
            <p className="text-gray-600">Nenhum projeto encontrado.</p>
          )}

          {filteredProjects.map((project) => {
            const progress = projectProgress[project.id] ?? 0;
            const status = progress === 100 ? "Concluído" : "Em andamento";

            return (
              <div
                key={project.id}
                className="flex justify-between items-center border border-gray-300 rounded-lg p-4 shadow-sm hover:bg-gray-50"
              >
                <div className="flex-1 flex flex-col">
                  {editingProjectId === project.id ? (
                    <>
                      <input
                        type="text"
                        value={editProjectData.name}
                        onChange={(e) =>
                          setEditProjectData({
                            ...editProjectData,
                            name: e.target.value
                          })
                        }
                        className="border px-2 py-1 rounded mb-1"
                      />
                      <input
                        type="text"
                        value={editProjectData.client}
                        onChange={(e) =>
                          setEditProjectData({
                            ...editProjectData,
                            client: e.target.value
                          })
                        }
                        className="border px-2 py-1 rounded mb-1"
                        placeholder="Cliente"
                      />
                      <input
                        type="text"
                        value={editProjectData.driveLink}
                        onChange={(e) =>
                          setEditProjectData({
                            ...editProjectData,
                            driveLink: e.target.value
                          })
                        }
                        className="border px-2 py-1 rounded"
                        placeholder="Link do Google Drive"
                      />
                    </>
                  ) : (
                    <div
                      className="cursor-pointer"
                      onClick={() => navigate(`/projects/${project.id}`)}
                    >
                      <h3 className="text-lg font-semibold text-gray-800">
                        {project.name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {project.client
                          ? `Cliente: ${project.client}`
                          : "Cliente não definido"}
                      </p>
                      <p className="text-xs text-gray-500">Status: {status}</p>
                    </div>
                  )}
                </div>

                {userData?.role === "admin" && (
                  <div className="flex gap-2 ml-4">
                    {editingProjectId === project.id ? (
                      <>
                        <button
                          className="text-blue-500 font-bold text-xl"
                          onClick={() => updateProject(project.id)}
                          title="Salvar alterações"
                        >
                          💾
                        </button>
                        <button
                          className="text-gray-500 font-bold text-xl"
                          onClick={() => setEditingProjectId(null)}
                          title="Cancelar"
                        >
                          ✖
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="text-yellow-500 font-bold text-xl"
                          onClick={() => startEditing(project)}
                          title="Editar projeto"
                        >
                          ✎
                        </button>
                        <button
                          className="text-green-500 font-bold text-xl"
                          onClick={() => cloneProject(project)}
                          title="Clonar projeto"
                        >
                          📄
                        </button>
                        <button
                          className="text-red-500 font-bold text-xl"
                          onClick={() => deleteProject(project.id)}
                          title="Deletar projeto"
                        >
                          ✕
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {userData?.role === "admin" && (
          addingNew ? (
            <div className="flex flex-col gap-2">
              <input
                type="text"
                placeholder="Nome do novo projeto"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                className="border px-3 py-2 rounded w-full"
              />
              <input
                type="text"
                placeholder="Link do Google Drive"
                value={newProjectDriveLink}
                onChange={(e) => setNewProjectDriveLink(e.target.value)}
                className="border px-3 py-2 rounded w-full"
              />
              <div className="flex gap-2">
                <button
                  onClick={addProject}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
                >
                  Adicionar
                </button>
                <button
                  onClick={() => {
                    setAddingNew(false);
                    setNewProjectName("");
                    setNewProjectDriveLink("");
                  }}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded"
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setAddingNew(true)}
              className="border-2 border-dashed border-gray-800 text-gray-800 font-semibold px-4 py-2 rounded w-full hover:bg-gray-100"
            >
              + Novo projeto
            </button>
          )
        )}
      </div>
    </div>
  );
};

export default ProjectSelection;
